interface AttentionOrder {
  status: string;
  orderDate: Date | string;
  requestedDeliveryDate?: Date | string;
  promisedDeliveryDate?: Date | string;
  priority?: string;
  hasBackorders?: boolean;
  backorderedItemCount?: number;
}

/** Rank the loaded order queue by the actions that can still change its outcome. */
export function prioritizeWorkspaceOrders<T extends AttentionOrder>(
  orders: readonly T[],
  now = Date.now()
): Array<{ order: T; reasons: string[] }> {
  const closed = new Set(['cancelled', 'delivered', 'shipped', 'fulfilled']);
  const today = new Date(now);
  today.setHours(0, 0, 0, 0);
  return orders
    .filter((order) => !closed.has(order.status))
    .map((order) => {
      const due = order.promisedDeliveryDate ?? order.requestedDeliveryDate;
      const dueAt = due ? new Date(due).getTime() : Infinity;
      const overdue = Number.isFinite(dueAt) && dueAt < today.getTime();
      const blocked = order.hasBackorders || (order.backorderedItemCount ?? 0) > 0;
      const highPriority = ['high', 'urgent', 'critical'].includes(order.priority ?? '');
      const reasons = [
        overdue && 'Delivery overdue',
        blocked && 'Backordered',
        highPriority && 'High priority',
      ].filter((reason): reason is string => typeof reason === 'string');
      if (!reasons.length) {
        reasons.push(
          order.status === 'pending' || order.status === 'draft'
            ? 'Awaiting processing'
            : 'Awaiting fulfillment'
        );
      }
      return {
        order,
        reasons,
        score: Number(overdue) * 100 + Number(blocked) * 80 + Number(highPriority) * 40,
        dueAt: Number.isFinite(dueAt) ? dueAt : Infinity,
      };
    })
    .sort(
      (left, right) =>
        right.score - left.score ||
        left.dueAt - right.dueAt ||
        new Date(left.order.orderDate).getTime() - new Date(right.order.orderDate).getTime()
    )
    .slice(0, 3)
    .map(({ order, reasons }) => ({ order, reasons }));
}
